const MiB = 1024 * 1024;

export const DATA_PORTABILITY_FORMAT = 'appyland-data-portability';
export const DATA_PORTABILITY_SCHEMA_VERSION = 1;
export const RESTORE_CONFIRMATION = 'RESTORE_LOCAL_DATA';

export const DEFAULT_PORTABILITY_POLICY = Object.freeze({
  maxSerializedBytes: 5 * MiB,
  maxDepth: 12,
  maxArrayItems: 10000,
  maxObjectKeys: 5000,
  maxStringLength: 100000,
});

const BLOCKED_KEYS = Object.freeze([
  '__proto__', 'prototype', 'constructor',
  'password', 'passcode', 'secret', 'clientsecret', 'privatekey',
  'token', 'accesstoken', 'refreshtoken', 'authorization', 'apikey',
  'ssn', 'socialsecuritynumber',
  'cardnumber', 'creditcard', 'debitcard', 'cvv', 'cvc',
  'routingnumber', 'bankaccount', 'bankaccountnumber', 'accountnumber',
  'medicalrecord', 'medicalrecordnumber', 'clientrecord', 'therapyrecord',
]);

export class DataPortabilityError extends Error {
  constructor(code, message, details = {}) {
    super(message);
    this.name = 'DataPortabilityError';
    this.code = code;
    Object.assign(this, details);
  }
}

function normalizePolicy(options = {}) {
  const policy = {
    maxSerializedBytes: options.maxSerializedBytes ?? DEFAULT_PORTABILITY_POLICY.maxSerializedBytes,
    maxDepth: options.maxDepth ?? DEFAULT_PORTABILITY_POLICY.maxDepth,
    maxArrayItems: options.maxArrayItems ?? DEFAULT_PORTABILITY_POLICY.maxArrayItems,
    maxObjectKeys: options.maxObjectKeys ?? DEFAULT_PORTABILITY_POLICY.maxObjectKeys,
    maxStringLength: options.maxStringLength ?? DEFAULT_PORTABILITY_POLICY.maxStringLength,
  };
  for (const [key, value] of Object.entries(policy)) {
    if (!Number.isInteger(value) || value <= 0) {
      throw new DataPortabilityError('invalid-policy', `Invalid portability policy value: ${key}.`);
    }
  }
  return Object.freeze(policy);
}

function cleanId(value, label) {
  const text = String(value ?? '').trim();
  if (!/^[a-z0-9][a-z0-9._-]{0,79}$/i.test(text)) {
    throw new DataPortabilityError('invalid-id', `${label} must use 1-80 safe letters, numbers, dots, dashes, or underscores.`);
  }
  return text;
}

function normalizedKey(key) {
  return String(key).toLowerCase().replace(/[^a-z0-9_]/g, '');
}

function isBlockedKey(key) {
  const normalized = normalizedKey(key);
  return BLOCKED_KEYS.some((blocked) => normalized === normalizedKey(blocked));
}

function isPlainObject(value) {
  if (!value || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function looksLikeRawMedia(value) {
  if (!value || typeof value !== 'object') return false;
  const name = value?.constructor?.name;
  if (name === 'Blob' || name === 'File' || name === 'ArrayBuffer') return true;
  if (typeof value.arrayBuffer === 'function' && Number.isFinite(value.size)) return true;
  return false;
}

function assertPortableValue(value, policy, path = '$', depth = 0, counters = { keys: 0 }) {
  if (depth > policy.maxDepth) {
    throw new DataPortabilityError('max-depth-exceeded', `Backup data is too deeply nested at ${path}.`);
  }
  if (value === null || typeof value === 'boolean' || typeof value === 'number') {
    if (typeof value === 'number' && !Number.isFinite(value)) {
      throw new DataPortabilityError('non-json-number', `Non-finite number is not portable at ${path}.`);
    }
    return;
  }
  if (typeof value === 'string') {
    if (value.length > policy.maxStringLength) {
      throw new DataPortabilityError('string-too-large', `Text value is too large at ${path}.`);
    }
    if (/^(data|blob):/i.test(value.trim())) {
      throw new DataPortabilityError('raw-media-reference-blocked', `Raw media/data URLs are not allowed in structured backups at ${path}.`);
    }
    return;
  }
  if (typeof value === 'undefined' || typeof value === 'function' || typeof value === 'symbol' || typeof value === 'bigint') {
    throw new DataPortabilityError('non-json-value', `Unsupported value type at ${path}.`);
  }
  if (looksLikeRawMedia(value)) {
    throw new DataPortabilityError('raw-media-blocked', `Raw media bytes are not allowed in this structured backup at ${path}.`);
  }
  if (Array.isArray(value)) {
    if (value.length > policy.maxArrayItems) {
      throw new DataPortabilityError('array-too-large', `Array exceeds the item limit at ${path}.`);
    }
    value.forEach((item, index) => assertPortableValue(item, policy, `${path}[${index}]`, depth + 1, counters));
    return;
  }
  if (!isPlainObject(value)) {
    throw new DataPortabilityError('non-plain-object', `Only plain JSON objects are allowed at ${path}.`);
  }
  const keys = Object.keys(value);
  counters.keys += keys.length;
  if (counters.keys > policy.maxObjectKeys) {
    throw new DataPortabilityError('too-many-object-keys', 'Backup data contains too many object fields.');
  }
  for (const key of keys) {
    if (isBlockedKey(key)) {
      throw new DataPortabilityError('sensitive-field-blocked', `Sensitive or unsafe field is blocked at ${path}.${key}.`, { field: key });
    }
    assertPortableValue(value[key], policy, `${path}.${key}`, depth + 1, counters);
  }
}

function stableStringify(value) {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
}

function utf8Bytes(text) {
  if (typeof TextEncoder === 'function') return new TextEncoder().encode(text);
  const bytes = [];
  for (const char of String(text)) {
    const cp = char.codePointAt(0);
    if (cp <= 0x7f) bytes.push(cp);
    else if (cp <= 0x7ff) bytes.push(0xc0 | (cp >> 6), 0x80 | (cp & 0x3f));
    else if (cp <= 0xffff) bytes.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f));
    else bytes.push(0xf0 | (cp >> 18), 0x80 | ((cp >> 12) & 0x3f), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f));
  }
  return Uint8Array.from(bytes);
}

function crc32Hex(text) {
  let crc = 0xffffffff;
  for (const byte of utf8Bytes(text)) {
    crc ^= byte;
    for (let i = 0; i < 8; i += 1) crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb88320 : 0);
  }
  return ((crc ^ 0xffffffff) >>> 0).toString(16).padStart(8, '0');
}

function checksumBody(envelope) {
  return stableStringify({
    format: envelope.format,
    schemaVersion: envelope.schemaVersion,
    appId: envelope.appId,
    dataVersion: envelope.dataVersion,
    createdAt: envelope.createdAt,
    payload: envelope.payload,
  });
}

function byteLength(text) {
  return utf8Bytes(text).byteLength;
}

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function normalizeValidationResult(result) {
  if (result === true || result == null) return { valid: true, errors: [] };
  if (result === false) return { valid: false, errors: ['Payload validation failed.'] };
  if (typeof result === 'object' && typeof result.valid === 'boolean') {
    const errors = Array.isArray(result.errors) ? result.errors.map(String) : (result.message ? [String(result.message)] : []);
    return { valid: result.valid, errors };
  }
  throw new DataPortabilityError('invalid-validator-result', 'validatePayload must return true/false or { valid, errors }.');
}

export function createDataBackup({ appId, dataVersion, payload, createdAt = Date.now() } = {}, options = {}) {
  const policy = normalizePolicy(options);
  const safeAppId = cleanId(appId, 'appId');
  const safeDataVersion = cleanId(dataVersion, 'dataVersion');
  if (!Number.isFinite(createdAt) || createdAt <= 0) {
    throw new DataPortabilityError('invalid-created-at', 'createdAt must be a positive timestamp.');
  }
  assertPortableValue(payload, policy);
  const envelope = {
    format: DATA_PORTABILITY_FORMAT,
    schemaVersion: DATA_PORTABILITY_SCHEMA_VERSION,
    appId: safeAppId,
    dataVersion: safeDataVersion,
    createdAt: Math.trunc(createdAt),
    checksum: { algorithm: 'crc32', value: '' },
    payload: cloneJson(payload),
  };
  envelope.checksum.value = crc32Hex(checksumBody(envelope));
  const serialized = JSON.stringify(envelope, null, 2);
  if (byteLength(serialized) > policy.maxSerializedBytes) {
    throw new DataPortabilityError('backup-too-large', 'Backup exceeds the configured local export size limit.');
  }
  return envelope;
}

export function serializeDataBackup(envelope, options = {}) {
  const inspected = inspectDataBackup(JSON.stringify(envelope), {}, options);
  return JSON.stringify(inspected.envelope, null, 2);
}

export function inspectDataBackup(text, { expectedAppId, validatePayload } = {}, options = {}) {
  const policy = normalizePolicy(options);
  if (typeof text !== 'string' || !text.trim()) {
    throw new DataPortabilityError('empty-backup', 'Backup text is empty.');
  }
  if (byteLength(text) > policy.maxSerializedBytes) {
    throw new DataPortabilityError('backup-too-large', 'Backup exceeds the configured local import size limit.');
  }
  let envelope;
  try { envelope = JSON.parse(text); }
  catch (cause) { throw new DataPortabilityError('invalid-json', 'Backup file is not valid JSON.', { cause }); }
  if (!isPlainObject(envelope) || envelope.format !== DATA_PORTABILITY_FORMAT) {
    throw new DataPortabilityError('wrong-format', 'This file is not an AppyLand data-portability backup.');
  }
  if (envelope.schemaVersion !== DATA_PORTABILITY_SCHEMA_VERSION) {
    throw new DataPortabilityError('unsupported-schema', `Unsupported backup schema version: ${envelope.schemaVersion}.`);
  }
  const appId = cleanId(envelope.appId, 'appId');
  const dataVersion = cleanId(envelope.dataVersion, 'dataVersion');
  if (expectedAppId && appId !== cleanId(expectedAppId, 'expectedAppId')) {
    throw new DataPortabilityError('wrong-app', `Backup belongs to ${appId}, not ${expectedAppId}.`);
  }
  if (!Number.isFinite(envelope.createdAt) || envelope.createdAt <= 0) {
    throw new DataPortabilityError('invalid-created-at', 'Backup has an invalid createdAt timestamp.');
  }
  if (!isPlainObject(envelope.checksum) || envelope.checksum.algorithm !== 'crc32' || !/^[0-9a-f]{8}$/i.test(envelope.checksum.value || '')) {
    throw new DataPortabilityError('invalid-checksum', 'Backup checksum metadata is missing or invalid.');
  }
  assertPortableValue(envelope.payload, policy);
  const expectedChecksum = crc32Hex(checksumBody(envelope));
  if (expectedChecksum !== String(envelope.checksum.value).toLowerCase()) {
    throw new DataPortabilityError('checksum-mismatch', 'Backup appears incomplete or changed. Do not restore it.');
  }
  let validation = { valid: true, errors: [] };
  if (validatePayload != null) {
    if (typeof validatePayload !== 'function') throw new DataPortabilityError('validator-required', 'validatePayload must be a function.');
    validation = normalizeValidationResult(validatePayload(cloneJson(envelope.payload), { appId, dataVersion }));
    if (!validation.valid) {
      throw new DataPortabilityError('payload-validation-failed', validation.errors[0] || 'Backup payload validation failed.', { errors: validation.errors });
    }
  }
  return {
    valid: true,
    appId,
    dataVersion,
    createdAt: envelope.createdAt,
    checksumVerified: true,
    checksumSecurity: 'corruption-detection-only',
    payload: cloneJson(envelope.payload),
    envelope: cloneJson(envelope),
  };
}

export async function restoreDataBackup(text, {
  expectedAppId,
  confirmation,
  validatePayload,
  snapshotCurrent,
  applyPayload,
  rollbackCurrent,
} = {}, options = {}) {
  if (confirmation !== RESTORE_CONFIRMATION) {
    throw new DataPortabilityError('restore-confirmation-required', `Exact confirmation ${RESTORE_CONFIRMATION} is required before restore.`);
  }
  if (typeof validatePayload !== 'function') {
    throw new DataPortabilityError('validator-required', 'A validatePayload function is required before restore.');
  }
  if (typeof applyPayload !== 'function') {
    throw new DataPortabilityError('apply-function-required', 'An applyPayload function is required before restore.');
  }
  const inspected = inspectDataBackup(text, { expectedAppId, validatePayload }, options);
  let snapshot;
  let hasSnapshot = false;
  if (snapshotCurrent != null) {
    if (typeof snapshotCurrent !== 'function') throw new DataPortabilityError('snapshot-function-invalid', 'snapshotCurrent must be a function.');
    snapshot = await snapshotCurrent();
    hasSnapshot = true;
  }
  try {
    await applyPayload(cloneJson(inspected.payload), {
      appId: inspected.appId,
      dataVersion: inspected.dataVersion,
      createdAt: inspected.createdAt,
    });
    return { restored: true, rolledBack: false, appId: inspected.appId, dataVersion: inspected.dataVersion };
  } catch (cause) {
    let rolledBack = false;
    let rollbackError = null;
    if (hasSnapshot && typeof rollbackCurrent === 'function') {
      try { await rollbackCurrent(snapshot); rolledBack = true; }
      catch (error) { rollbackError = error; }
    }
    throw new DataPortabilityError(
      'restore-apply-failed',
      rolledBack ? 'Restore failed and the prior local state was restored.' : 'Restore failed. The app must report whether current local state changed.',
      { cause, rolledBack, rollbackError },
    );
  }
}

export async function readLocalBackupFile(file, options = {}) {
  const policy = normalizePolicy(options);
  if (!file || typeof file.text !== 'function' || !Number.isFinite(file.size)) {
    throw new DataPortabilityError('invalid-file', 'Choose a local JSON backup file.');
  }
  if (file.size <= 0) throw new DataPortabilityError('empty-file', 'Backup file is empty.');
  if (file.size > policy.maxSerializedBytes) throw new DataPortabilityError('backup-too-large', 'Backup file exceeds the local import size limit.');
  if (file.type && !['application/json', 'text/json', 'text/plain'].includes(String(file.type).toLowerCase())) {
    throw new DataPortabilityError('file-type-not-allowed', 'Backup file must be JSON text.');
  }
  const text = await file.text();
  if (byteLength(text) > policy.maxSerializedBytes) throw new DataPortabilityError('backup-too-large', 'Decoded backup exceeds the local import size limit.');
  return text;
}

export function downloadDataBackup(envelope, { filename = 'appyland-local-backup.json' } = {}, options = {}) {
  const text = serializeDataBackup(envelope, options);
  if (typeof document === 'undefined' || typeof Blob !== 'function' || !globalThis.URL?.createObjectURL) {
    throw new DataPortabilityError('download-unsupported', 'Local browser download is unavailable.');
  }
  const safeName = String(filename || 'appyland-local-backup.json').replace(/[^a-z0-9._-]+/gi, '-').replace(/^-+|-+$/g, '').slice(0, 100) || 'appyland-local-backup.json';
  const finalName = safeName.toLowerCase().endsWith('.json') ? safeName : `${safeName}.json`;
  const blob = new Blob([text], { type: 'application/json;charset=utf-8' });
  const url = globalThis.URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = finalName;
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    globalThis.URL.revokeObjectURL(url);
  }
  return { downloaded: true, filename: finalName, bytes: byteLength(text) };
}
